import React from 'react';
import { createPortal } from 'react-dom';
import { Box, getVariant, responsive } from '@apia/theme';
import { Accordion, AccordionItem, useAccordionContext } from '../accordion';
import { Checkbox } from '../forms/Checkbox';
import { DateInput } from '../forms/DateInput';
import { IconButton } from '../forms/buttons/IconButton';
import { SimpleButton } from '../forms/buttons/SimpleButton';

export type TBPMGraphViewerTaskDefinition = {
  id: string;
  title?: string;
  aliases?: string[];
  forms?: string[];
  performer?: string;
};

export type TBPMGraphViewerTaskFormDefinition = {
  fields: TBPMGraphViewerTaskFormFieldDefinition[];
  name: string;
  title?: string;
};

export type TBPMGraphViewerTaskFormFieldDefinition = {
  attributeId: string;
  disabled?: boolean;
  entityId?: string;
  gridId?: string;
  gridTitle?: string;
  hidden?: boolean;
  readonly?: boolean;
  required?: boolean;
  title?: string;
  type: string;
};

export type TBPMGraphViewerAttributeDefinition = {
  name: string;
  title?: string;
  type?: string;
};

export type TBPMGraphViewerEntityDefinition = {
  name: string;
  options: Array<{
    name: string;
    title?: string;
  }>;
  title?: string;
};

export type TBPMGraphViewerLabels = {
  addGridRow: string;
  closeFullscreen: string;
  deleteGridRow: string;
  fileInfo: string;
  incomingLegend: string;
  maximizeGrid: string;
  noTaskForms: string;
  noVisibleFields: string;
  openFullscreen: string;
  outgoingLegend: string;
  readonlyForm: string;
  required: string;
  resetZoom: string;
  restoreGrid: string;
  uploadFile: string;
};

export type TBPMGraphViewerProps = {
  alt: string;
  attributes?: TBPMGraphViewerAttributeDefinition[];
  className?: string;
  entities?: TBPMGraphViewerEntityDefinition[];
  forms?: TBPMGraphViewerTaskFormDefinition[];
  fullscreen?: boolean;
  fullscreenMode?: 'external' | 'inline' | 'portal';
  imageSrc?: string;
  labels?: Partial<TBPMGraphViewerLabels>;
  onFullscreenChange?: (fullscreen: boolean) => void;
  renderTaskPreview?: (task: TBPMGraphViewerTaskDefinition) => React.ReactNode;
  svgContent?: string;
  taskEdgeHoverEnabled?: boolean;
  taskPreviewEnabled?: boolean;
  taskPreviewEmpty?: React.ReactNode;
  tasks?: TBPMGraphViewerTaskDefinition[];
};

export type TBPMGraphFormPreviewProps = {
  attributes?: TBPMGraphViewerAttributeDefinition[];
  className?: string;
  embedded?: boolean;
  entities?: TBPMGraphViewerEntityDefinition[];
  forceReadonly?: boolean;
  form: TBPMGraphViewerTaskFormDefinition;
  labels?: Partial<TBPMGraphViewerLabels>;
  stateKey?: string;
};

type TApiaThemeWindow = Window & {
  SHOW_REQUIRED_POSITION?: string;
};

type TBPMGraphViewerFormPreviewType =
  | 'button'
  | 'check'
  | 'combo'
  | 'editor'
  | 'file'
  | 'input'
  | 'radio'
  | 'textarea';

type TBPMGraphViewerFormPreviewItem =
  | { field: TBPMGraphViewerTaskFormFieldDefinition; type: 'field' }
  | {
      fields: TBPMGraphViewerTaskFormFieldDefinition[];
      name: string;
      title: string;
      type: 'grid';
    };

type TMapViewport = {
  scale: number;
  x: number;
  y: number;
};

type TBpmnMapHoverModifiers = {
  ctrlKey?: boolean;
  shiftKey?: boolean;
};

type TLegacyBpmnMapNode = {
  id: string;
  x: number;
  y: number;
};

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const WHEEL_ZOOM_SENSITIVITY = 0.0015;
const MIN_MAP_SCALE = 0.25;
const MAX_MAP_SCALE = 4;
const BPMN_TASK_FOCUS_WIDTH_RATIO = 0.12;
const BPMN_TASK_FOCUS_ANIMATION_MS = 300;
const BPMN_TASK_TITLE_MAX_CHARS = 14;
const BPMN_TASK_MAX_LINES = 4;
const BPMN_TASK_TEXT_FONT_SIZE = 13;
const BPMN_TASK_TEXT_LINE_HEIGHT = 15;
const BPMN_MAP_STICKY_LANE_BACKGROUND =
  'var(--theme-ui-colors-palette-background-paper)';
const CLICK_SELECTION_DELAY_MS = 180;
const DEFAULT_MAP_VIEWPORT = { scale: 1, x: 0, y: 0 };
const BPMN_MAP_EDGE_SHAPE_SELECTOR = 'path, polyline, line';
const BPMN_MAP_EDGE_LABEL_SELECTOR =
  '.bpa-map-edge-label, .bpa-map-edge-label-group';
const BPMN_MAP_STICKY_LANE_SELECTOR = '[data-bpa-map-sticky-lane="true"]';
const FORM_PREVIEW_COLUMNS = 4;
const DEFAULT_BPM_GRAPH_VIEWER_LABELS: TBPMGraphViewerLabels = {
  addGridRow: 'Agregar fila',
  closeFullscreen: 'Cerrar mapa',
  deleteGridRow: 'Eliminar fila',
  fileInfo: 'Informacion',
  incomingLegend: 'Verde: caminos entrantes',
  maximizeGrid: 'Maximizar grilla',
  noTaskForms: 'Sin formularios asociados.',
  noVisibleFields: 'Sin campos visibles.',
  openFullscreen: 'Abrir mapa en pantalla completa',
  outgoingLegend: 'Celeste: caminos salientes',
  readonlyForm: 'Solo lectura',
  required: 'Requerido',
  resetZoom: 'Restablecer zoom',
  restoreGrid: 'Restaurar grilla',
  uploadFile: 'Subir archivo',
};
const BPMGraphViewerLabelsContext =
  React.createContext<TBPMGraphViewerLabels>(DEFAULT_BPM_GRAPH_VIEWER_LABELS);
const FORM_PREVIEW_FIELD_STYLE_MAP: Record<
  TBPMGraphViewerFormPreviewType,
  string
> = {
  button: 'button',
  check: 'check',
  combo: 'select',
  editor: 'area',
  file: 'file',
  input: 'input',
  radio: 'radio',
  textarea: 'area',
};
const bpmGraphFormPreviewCollapsedByStateKey = new Map<string, boolean>();

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

const compareText = (left: string, right: string) =>
  left.localeCompare(right, undefined, {
    numeric: true,
    sensitivity: 'base',
  });

const sortByText = <T,>(items: T[], getter: (item: T) => string) =>
  [...items].sort((left, right) => compareText(getter(left), getter(right)));

const displayName = (title: string | undefined, name: string | undefined) =>
  title || name || '-';

const useBPMGraphViewerLabels = () =>
  React.useContext(BPMGraphViewerLabelsContext);

const bpmGraphFormLookupKeys = (
  form: Pick<TBPMGraphViewerTaskFormDefinition, 'name' | 'title'>,
) =>
  Array.from(
    new Set([form.name, form.title].filter((value): value is string =>
      Boolean(value?.trim()),
    )),
  );

const svgToDataUrl = (svg: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

function svgContentFromImageSrc(src?: string) {
  const clean = (src ?? '').trim();

  if (clean.startsWith('<svg')) {
    return clean;
  }

  if (!clean.startsWith('data:image/svg+xml')) {
    return undefined;
  }

  const commaIndex = clean.indexOf(',');
  if (commaIndex < 0) {
    return undefined;
  }

  try {
    const metadata = clean.slice(0, commaIndex);
    const body = clean.slice(commaIndex + 1);
    const svg = metadata.toLowerCase().includes(';base64')
      ? globalThis.atob(body)
      : decodeURIComponent(body);

    return svg.trim().startsWith('<svg') ? svg : undefined;
  } catch {
    return undefined;
  }
}

function numberAttribute(element: Element, name: string) {
  const value = Number(element.getAttribute(name));
  return Number.isFinite(value) ? value : undefined;
}

function normalizeBpmnMapSvgRootSizing(svg: Element) {
  const width = numberAttribute(svg, 'width');
  const height = numberAttribute(svg, 'height');

  if (!svg.getAttribute('viewBox') && width && height) {
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }

  svg.removeAttribute('width');
  svg.removeAttribute('height');

  if (!svg.getAttribute('preserveAspectRatio')) {
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  }

  const style = (svg.getAttribute('style') ?? '')
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .filter(
      (part) =>
        !/^(display|width|height|max-width|max-height|min-width|min-height)\s*:/i.test(
          part,
        ),
    );

  style.push(
    'display: block',
    'width: 100%',
    'height: 100%',
    'max-width: 100%',
    'max-height: 100%',
    'min-width: 0',
    'min-height: 0',
  );
  svg.setAttribute('style', style.join('; '));
}

function setStickyLaneElementOrigin(
  element: Element,
  kind: 'rect' | 'text',
  x: number,
  offsetX = 0,
) {
  appendClassName(element, 'bpa-map-sticky-lane');
  element.setAttribute('data-bpa-map-sticky-lane', 'true');
  element.setAttribute('data-bpa-map-sticky-lane-kind', kind);
  element.setAttribute('data-bpa-map-sticky-origin-x', String(x));
  element.setAttribute('data-bpa-map-sticky-offset-x', String(offsetX));
}

function setBpmnMapTooltip(element: Element, text?: string | null) {
  const tooltip = compactBpmnMapText(text);
  if (!tooltip) {
    return;
  }

  const existingTitle = Array.from(element.childNodes).find(
    (node): node is Element =>
      node.nodeType === Node.ELEMENT_NODE &&
      (node as Element).tagName.toLowerCase() === 'title',
  );
  const title =
    existingTitle ??
    element.ownerDocument?.createElementNS(SVG_NAMESPACE, 'title');

  if (!title) {
    return;
  }

  title.textContent = tooltip;
  if (!existingTitle) {
    element.insertBefore(title, element.firstChild);
  }
}

function markBpmnMapStickyLaneHeaders(svg: Element) {
  const rects = Array.from(svg.querySelectorAll('rect'));

  rects.forEach((rect) => {
    const width = numberAttribute(rect, 'width');
    const height = numberAttribute(rect, 'height');
    const x = numberAttribute(rect, 'x');
    const y = numberAttribute(rect, 'y');

    if (
      width === undefined ||
      height === undefined ||
      x === undefined ||
      y === undefined ||
      width > 180 ||
      height < 100 ||
      rect.closest('[data-bpa-map-kind]')
    ) {
      return;
    }

    const stickyTexts = Array.from(
      svg.querySelectorAll('text[transform^="rotate(-90"]'),
    ).filter((text) => {
      const textX = numberAttribute(text, 'x');
      const textY = numberAttribute(text, 'y');

      return !(
        textX === undefined ||
        textY === undefined ||
        textX < x ||
        textX > x + width ||
        textY < y ||
        textY > y + height
      );
    });

    const laneTitle = stickyTexts
      .map((text) => text.textContent ?? '')
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    setStickyLaneElementOrigin(rect, 'rect', x);
    rect.setAttribute('data-bpa-map-lane-label', laneTitle);
    if (!isLikelyTruncatedBpmnText(laneTitle)) {
      setBpmnMapTooltip(rect, laneTitle);
    }
    rect.setAttribute('fill', BPMN_MAP_STICKY_LANE_BACKGROUND);
    rect.setAttribute('fill-opacity', '1');
    stickyTexts.forEach((text) => {
      const textX = numberAttribute(text, 'x');
      if (textX !== undefined) {
        setStickyLaneElementOrigin(text, 'text', x, textX - x);
      }
    });

    svg.appendChild(rect);
    stickyTexts.forEach((text) => svg.appendChild(text));
  });
}

function updateStickyBpmnMapLaneElement(element: Element, nextX: number) {
  const kind = element.getAttribute('data-bpa-map-sticky-lane-kind');
  const offsetX =
    numberAttribute(element, 'data-bpa-map-sticky-offset-x') ?? 0;
  const elementX = nextX + offsetX;
  element.setAttribute('x', String(elementX));

  if (kind !== 'text') {
    return;
  }

  const y = numberAttribute(element, 'y');
  if (y !== undefined) {
    element.setAttribute('transform', `rotate(-90 ${elementX} ${y})`);
  }
}

function updateStickyBpmnMapLanes(root: Element | null) {
  const svg = root?.querySelector('svg');
  const viewport = root?.closest('.bpmGraphViewer__viewport');
  if (!(svg instanceof SVGSVGElement) || !viewport) {
    return;
  }

  const viewBox = svg.viewBox.baseVal;
  const svgRect = svg.getBoundingClientRect();
  const viewportRect = viewport.getBoundingClientRect();
  if (svgRect.width <= 0 || viewBox.width <= 0) {
    return;
  }

  const unitsPerPixel = viewBox.width / svgRect.width;
  const visibleLeft =
    viewBox.x + (viewportRect.left - svgRect.left) * unitsPerPixel;

  Array.from(svg.querySelectorAll(BPMN_MAP_STICKY_LANE_SELECTOR)).forEach(
    (element) => {
      const originX = numberAttribute(element, 'data-bpa-map-sticky-origin-x');
      if (originX === undefined) {
        return;
      }

      updateStickyBpmnMapLaneElement(element, Math.max(originX, visibleLeft));
    },
  );
}

function pointsAttributeToPoints(points: string) {
  return points
    .trim()
    .split(/\s+/)
    .map((point) => {
      const [x, y] = point.split(',').map(Number);
      return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
    })
    .filter(Boolean) as Array<{ x: number; y: number }>;
}

function appendClassName(element: Element, className: string) {
  const currentClassName = element.getAttribute('class') ?? '';

  if (currentClassName.split(/\s+/).includes(className)) {
    return;
  }

  element.setAttribute('class', `${currentClassName} ${className}`.trim());
}

function svgNumberList(value: string) {
  return (
    value
      .match(/-?\d+(?:\.\d+)?/g)
      ?.map(Number)
      .filter(Number.isFinite) ?? []
  );
}

function bpmnMapEdgeShapePoints(edgeShape: Element) {
  const tagName = edgeShape.tagName.toLowerCase();

  if (tagName === 'polyline') {
    const points = pointsAttributeToPoints(
      edgeShape.getAttribute('points') ?? '',
    );
    return points.length > 1
      ? { firstPoint: points[0], lastPoint: points[points.length - 1] }
      : null;
  }

  if (tagName === 'line') {
    const x1 = numberAttribute(edgeShape, 'x1');
    const y1 = numberAttribute(edgeShape, 'y1');
    const x2 = numberAttribute(edgeShape, 'x2');
    const y2 = numberAttribute(edgeShape, 'y2');

    return x1 !== undefined &&
      y1 !== undefined &&
      x2 !== undefined &&
      y2 !== undefined
      ? {
          firstPoint: { x: x1, y: y1 },
          lastPoint: { x: x2, y: y2 },
        }
      : null;
  }

  if (tagName === 'path') {
    const values = svgNumberList(edgeShape.getAttribute('d') ?? '');

    return values.length >= 4
      ? {
          firstPoint: { x: values[0], y: values[1] },
          lastPoint: {
            x: values[values.length - 2],
            y: values[values.length - 1],
          },
        }
      : null;
  }

  return null;
}

function enableBpmnMapLabelPointerEvents(label: Element) {
  const elements = [
    label,
    ...Array.from(label.querySelectorAll('rect, text, tspan')),
  ];

  elements.forEach((element) => {
    const styled = element as Element & { style?: CSSStyleDeclaration };
    element.setAttribute('pointer-events', 'all');

    if (styled.style) {
      styled.style.pointerEvents = 'all';
    }
  });
}

function normalizeBpmnMapText(value?: string | null) {
  return (value ?? '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

function compactBpmnMapText(value?: string | null) {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

function isLikelyTruncatedBpmnText(value?: string | null) {
  return /(?:\.{3}|…)$/.test(compactBpmnMapText(value));
}

function getBpmnMapLabelGroupText(labelGroup: Element) {
  const tspanParts = Array.from(labelGroup.querySelectorAll('tspan'))
    .map((element) => element.textContent ?? '')
    .filter(Boolean);
  const textParts =
    tspanParts.length > 0
      ? tspanParts
      : Array.from(labelGroup.querySelectorAll('text'))
          .map((element) => element.textContent ?? '')
          .filter(Boolean);

  return normalizeBpmnMapText(textParts.join(' '));
}

function createBpmnMapEdgeHitShape(edgeShape: Element) {
  const hitShape = edgeShape.cloneNode(false) as Element;

  hitShape.setAttribute('class', 'bpa-map-edge-hit');
  hitShape.removeAttribute('style');
  hitShape.setAttribute('fill', 'none');
  hitShape.setAttribute('stroke', 'transparent');
  hitShape.setAttribute('stroke-width', '14');
  hitShape.setAttribute('stroke-linecap', 'round');
  hitShape.setAttribute('stroke-linejoin', 'round');
  hitShape.removeAttribute('marker-end');

  return hitShape;
}

function getBpmnMapNodeId(node: Element) {
  return (
    node.getAttribute('data-bpa-map-node-id') ??
    node.getAttribute('data-bpa-map-activity-id') ??
    node.getAttribute('id') ??
    ''
  ).trim();
}

function getBpmnMapNodeText(node: Element) {
  const textParts = Array.from(node.querySelectorAll('text, tspan'))
    .map((element) => element.textContent ?? '')
    .filter(Boolean);

  return textParts.join(' ').replace(/\s+/g, ' ').trim();
}

function normalizeBpmnMapNodeIds(svg: Element) {
  Array.from(
    svg.querySelectorAll(
      '[data-bpa-map-kind="task"], [data-bpa-map-kind="gateway"]',
    ),
  ).forEach((node) => {
    const nodeId = getBpmnMapNodeId(node);

    if (nodeId) {
      node.setAttribute('data-bpa-map-node-id', nodeId);
    }
  });
}

function normalizeBpmnMapTaskLabels(svg: Element) {
  Array.from(svg.querySelectorAll('[data-bpa-map-kind="task"]')).forEach(
    (task) => {
      if (task.getAttribute('data-bpa-map-task-label')) {
        return;
      }

      const label = getBpmnMapNodeText(task);

      if (label) {
        task.setAttribute('data-bpa-map-task-label', label);
      }
    },
  );
}

function normalizeBpmnMapTaskTooltips(svg: Element) {
  Array.from(svg.querySelectorAll('[data-bpa-map-kind="task"]')).forEach(
    (task) => {
      const label =
        task.getAttribute('data-bpa-map-task-label') ||
        task.getAttribute('data-bpa-map-task-name') ||
        getBpmnMapNodeText(task);

      if (isLikelyTruncatedBpmnText(label)) {
        return;
      }

      setBpmnMapTooltip(task, label);
      Array.from(task.querySelectorAll('rect')).forEach((shape) => {
        setBpmnMapTooltip(shape, label);
      });
    },
  );
}

function setBpmnMapEdgeTaskIds(
  edge: Element,
  direction: 'incoming' | 'outgoing',
  taskIds: Set<string>,
) {
  const attrName = `data-bpa-map-${direction}-task-ids`;

  if (taskIds.size > 0) {
    edge.setAttribute(attrName, JSON.stringify(Array.from(taskIds)));
  } else {
    edge.removeAttribute(attrName);
  }
}

function setBpmnMapEdgePathIds(
  edge: Element,
  direction: 'incoming' | 'outgoing',
  edgeIds: Set<string>,
) {
  const attrName = `data-bpa-map-${direction}-edge-ids`;

  if (edgeIds.size > 0) {
    edge.setAttribute(attrName, JSON.stringify(Array.from(edgeIds)));
  } else {
    edge.removeAttribute(attrName);
  }
}

function normalizeBpmnMapEdgeTaskPaths(svg: Element) {
  const nodesById = new Map<string, { element: Element; kind: string }>();
  const edges = Array.from(svg.querySelectorAll('[data-bpa-map-kind="edge"]'));
  const edgeRefs = edges.map((edge) => ({
    edge,
    edgeId: (edge.getAttribute('data-bpa-map-edge-id') ?? '').trim(),
    from: (edge.getAttribute('data-bpa-map-from') ?? '').trim(),
    to: (edge.getAttribute('data-bpa-map-to') ?? '').trim(),
  }));

  Array.from(
    svg.querySelectorAll(
      '[data-bpa-map-kind="task"], [data-bpa-map-kind="gateway"]',
    ),
  ).forEach((node) => {
    const nodeId = getBpmnMapNodeId(node);

    if (nodeId) {
      nodesById.set(nodeId, {
        element: node,
        kind: node.getAttribute('data-bpa-map-kind') ?? '',
      });
    }
  });

  const collectIncomingTasks = (
    nodeId: string,
    visited = new Set<string>(),
  ): { edgeIds: Set<string>; taskIds: Set<string> } => {
    const edgeIds = new Set<string>();
    const taskIds = new Set<string>();

    if (!nodeId || visited.has(nodeId)) {
      return { edgeIds, taskIds };
    }

    const node = nodesById.get(nodeId);
    if (node?.kind === 'task') {
      taskIds.add(nodeId);
      return { edgeIds, taskIds };
    }

    const nextVisited = new Set(visited);
    nextVisited.add(nodeId);
    edgeRefs.forEach((edgeRef) => {
      if (edgeRef.to !== nodeId) {
        return;
      }

      const result = collectIncomingTasks(edgeRef.from, nextVisited);
      if (result.taskIds.size > 0 && edgeRef.edgeId) {
        edgeIds.add(edgeRef.edgeId);
      }

      result.edgeIds.forEach((edgeId) => edgeIds.add(edgeId));
      result.taskIds.forEach((taskId) => taskIds.add(taskId));
    });

    return { edgeIds, taskIds };
  };

  const collectOutgoingTasks = (
    nodeId: string,
    visited = new Set<string>(),
  ): { edgeIds: Set<string>; taskIds: Set<string> } => {
    const edgeIds = new Set<string>();
    const taskIds = new Set<string>();

    if (!nodeId || visited.has(nodeId)) {
      return { edgeIds, taskIds };
    }

    const node = nodesById.get(nodeId);
    if (node?.kind === 'task') {
      taskIds.add(nodeId);
      return { edgeIds, taskIds };
    }

    const nextVisited = new Set(visited);
    nextVisited.add(nodeId);
    edgeRefs.forEach((edgeRef) => {
      if (edgeRef.from !== nodeId) {
        return;
      }

      const result = collectOutgoingTasks(edgeRef.to, nextVisited);
      if (result.taskIds.size > 0 && edgeRef.edgeId) {
        edgeIds.add(edgeRef.edgeId);
      }

      result.edgeIds.forEach((edgeId) => edgeIds.add(edgeId));
      result.taskIds.forEach((taskId) => taskIds.add(taskId));
    });

    return { edgeIds, taskIds };
  };

  edgeRefs.forEach((edgeRef) => {
    const incoming = collectIncomingTasks(edgeRef.from);
    const outgoing = collectOutgoingTasks(edgeRef.to);

    setBpmnMapEdgeTaskIds(edgeRef.edge, 'incoming', incoming.taskIds);
    setBpmnMapEdgePathIds(edgeRef.edge, 'incoming', incoming.edgeIds);
    setBpmnMapEdgeTaskIds(edgeRef.edge, 'outgoing', outgoing.taskIds);
    setBpmnMapEdgePathIds(edgeRef.edge, 'outgoing', outgoing.edgeIds);
  });
}

function normalizeBpmnMapEdges(svg: Element) {
  normalizeBpmnMapNodeIds(svg);
  normalizeBpmnMapTaskLabels(svg);
  normalizeBpmnMapTaskTooltips(svg);

  const edges = Array.from(svg.querySelectorAll('[data-bpa-map-kind="edge"]'));
  const edgesByCondition = new Map<string, Element[]>();

  edges.forEach((edge, index) => {
    const edgeId =
      edge.getAttribute('data-bpa-map-edge-id') ?? `bpa-map-edge-${index}`;
    const condition = normalizeBpmnMapText(
      edge.getAttribute('data-bpa-map-condition'),
    );
    const edgeShapes = Array.from(
      edge.querySelectorAll(BPMN_MAP_EDGE_SHAPE_SELECTOR),
    ).filter((shape) => !shape.classList.contains('bpa-map-edge-hit'));
    const hasHitShape = Boolean(edge.querySelector('.bpa-map-edge-hit'));

    appendClassName(edge, 'bpa-map-edge');
    edge.setAttribute('data-bpa-map-edge-id', edgeId);

    if (condition) {
      const conditionEdges = edgesByCondition.get(condition) ?? [];
      conditionEdges.push(edge);
      edgesByCondition.set(condition, conditionEdges);
    }

    edgeShapes.forEach((shape) => {
      appendClassName(shape, 'bpa-map-edge-line');
      shape.setAttribute('data-bpa-map-edge-id', edgeId);

      if (!hasHitShape && shape.parentNode) {
        shape.parentNode.insertBefore(createBpmnMapEdgeHitShape(shape), shape);
      }
    });

    Array.from(edge.querySelectorAll('.bpa-map-edge-label-group')).forEach(
      (labelGroup) => {
        labelGroup.setAttribute('data-bpa-map-edge-id', edgeId);
        enableBpmnMapLabelPointerEvents(labelGroup);
      },
    );

    Array.from(edge.querySelectorAll('text')).forEach((label) => {
      appendClassName(label, 'bpa-map-edge-label');
      label.setAttribute('data-bpa-map-edge-id', edgeId);
      enableBpmnMapLabelPointerEvents(label);
    });
  });

  const externalLabelGroups = Array.from(
    svg.querySelectorAll('.bpa-map-edge-label-group'),
  ).filter((labelGroup) => !labelGroup.closest('[data-bpa-map-kind="edge"]'));

  externalLabelGroups.forEach((labelGroup) => {
    if (labelGroup.getAttribute('data-bpa-map-edge-id')) {
      return;
    }

    const labelText = getBpmnMapLabelGroupText(labelGroup);
    const edge = labelText ? edgesByCondition.get(labelText)?.shift() : null;
    const edgeId = edge?.getAttribute('data-bpa-map-edge-id');

    if (!edgeId) {
      return;
    }

    labelGroup.setAttribute('data-bpa-map-edge-id', edgeId);
    enableBpmnMapLabelPointerEvents(labelGroup);
    Array.from(labelGroup.querySelectorAll('rect, text, tspan')).forEach(
      (labelPart) => {
        if (labelPart.tagName.toLowerCase() === 'text') {
          appendClassName(labelPart, 'bpa-map-edge-label');
        }

        labelPart.setAttribute('data-bpa-map-edge-id', edgeId);
        enableBpmnMapLabelPointerEvents(labelPart);
      },
    );
  });

  normalizeBpmnMapEdgeTaskPaths(svg);
}

function centerFromPolygon(polygon: Element) {
  const points = pointsAttributeToPoints(polygon.getAttribute('points') ?? '');

  if (points.length === 0) {
    return null;
  }

  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  };
}

function wrapBpmnMapText(text: string, maxChars: number, maxLines: number) {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    if (lines.length >= maxLines) {
      return;
    }

    if (!current) {
      current = word;
      return;
    }

    if (current.length + 1 + word.length <= maxChars) {
      current = `${current} ${word}`;
      return;
    }

    lines.push(current);
    current = word;
  });

  if (current && lines.length < maxLines) {
    lines.push(current);
  }

  return lines.length > 0 ? lines : [text];
}

function normalizeLegacyTaskText(
  svg: Element,
  rect: Element,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  const centerX = x + width / 2;
  const textElements = Array.from(svg.querySelectorAll('text.bpa-map-text'))
    .filter((textElement) => !textElement.getAttribute('transform'))
    .filter((textElement) => {
      const textX = numberAttribute(textElement, 'x');
      const textY = numberAttribute(textElement, 'y');

      return (
        textX !== undefined &&
        textY !== undefined &&
        Math.abs(textX - centerX) <= width / 2 &&
        textY >= y &&
        textY <= y + height + BPMN_TASK_TEXT_LINE_HEIGHT
      );
    })
    .sort(
      (left, right) =>
        (numberAttribute(left, 'y') ?? 0) - (numberAttribute(right, 'y') ?? 0),
    );

  if (textElements.length === 0) {
    return;
  }

  const text = textElements
    .map((textElement) => textElement.textContent ?? '')
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  rect.setAttribute('data-bpa-map-task-label', text);

  const lines = wrapBpmnMapText(
    text,
    BPMN_TASK_TITLE_MAX_CHARS,
    BPMN_TASK_MAX_LINES,
  );
  const firstY =
    y +
    (height - BPMN_TASK_TEXT_LINE_HEIGHT * lines.length) / 2 +
    BPMN_TASK_TEXT_FONT_SIZE;
  const firstTextElement = textElements[0];
  const parent = firstTextElement.parentNode ?? rect.parentNode;

  if (!parent) {
    return;
  }

  textElements.slice(lines.length).forEach((textElement) => {
    textElement.parentNode?.removeChild(textElement);
  });

  lines.forEach((line, index) => {
    const textElement =
      textElements[index] ?? (firstTextElement.cloneNode(false) as Element);

    textElement.textContent = line;
    textElement.setAttribute('x', String(centerX));
    textElement.setAttribute(
      'y',
      String(firstY + BPMN_TASK_TEXT_LINE_HEIGHT * index),
    );
    textElement.setAttribute('text-anchor', 'middle');
    textElement.setAttribute('font-size', String(BPMN_TASK_TEXT_FONT_SIZE));

    if (!textElement.parentNode) {
      parent.appendChild(textElement);
    }
  });
}

function findLegacyBpmnMapNode(
  nodes: TLegacyBpmnMapNode[],
  point: { x: number; y: number },
) {
  let bestNode: TLegacyBpmnMapNode | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;

  nodes.forEach((node) => {
    const distance = Math.hypot(node.x - point.x, node.y - point.y);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestNode = node;
    }
  });

  return bestDistance <= 170 ? bestNode : undefined;
}

function addLegacyBpmnMapNode(
  nodes: TLegacyBpmnMapNode[],
  point: { x: number; y: number },
  prefix: string,
  id?: string | null,
) {
  if (id) {
    const existingById = nodes.find((node) => node.id === id);

    if (existingById) {
      return existingById;
    }
  }

  const existing = nodes.find(
    (node) => Math.hypot(node.x - point.x, node.y - point.y) < 2,
  );

  if (existing) {
    return existing;
  }

  const node = {
    id: id ?? `${prefix}-${nodes.length}`,
    x: point.x,
    y: point.y,
  };
  nodes.push(node);
  return node;
}

function normalizeLegacyBpmnMapSvg(svgContent: string) {
  if (
    !svgContent ||
    typeof DOMParser === 'undefined' ||
    typeof XMLSerializer === 'undefined'
  ) {
    return svgContent;
  }

  try {
    const document = new DOMParser().parseFromString(
      svgContent,
      'image/svg+xml',
    );
    const svg = document.documentElement;

    if (!svg || svg.nodeName.toLowerCase() !== 'svg') {
      return svgContent;
    }

    normalizeBpmnMapSvgRootSizing(svg);

    const nodes: TLegacyBpmnMapNode[] = [];
    svg.querySelectorAll('marker').forEach((marker) => {
      marker.setAttribute('markerUnits', 'userSpaceOnUse');
      marker.setAttribute('markerWidth', '16');
      marker.setAttribute('markerHeight', '16');
    });
    normalizeBpmnMapEdges(svg);

    const inferNodeIdFromEdges = (point: { x: number; y: number }) => {
      const candidateIds: string[] = [];

      Array.from(svg.querySelectorAll('[data-bpa-map-kind="edge"]')).forEach(
        (edge) => {
          const edgeShape = Array.from(
            edge.querySelectorAll(BPMN_MAP_EDGE_SHAPE_SELECTOR),
          ).find((shape) => !shape.classList.contains('bpa-map-edge-hit'));
          const points = edgeShape ? bpmnMapEdgeShapePoints(edgeShape) : null;
          const firstPoint = points?.firstPoint;
          const lastPoint = points?.lastPoint;

          if (
            firstPoint &&
            Math.hypot(firstPoint.x - point.x, firstPoint.y - point.y) <= 45
          ) {
            const from = edge.getAttribute('data-bpa-map-from');

            if (from) {
              candidateIds.push(from);
            }
          }

          if (
            lastPoint &&
            Math.hypot(lastPoint.x - point.x, lastPoint.y - point.y) <= 45
          ) {
            const to = edge.getAttribute('data-bpa-map-to');

            if (to) {
              candidateIds.push(to);
            }
          }
        },
      );

      return candidateIds[0] ?? null;
    };

    Array.from(svg.querySelectorAll('rect')).forEach((rect) => {
      const width = numberAttribute(rect, 'width');
      const height = numberAttribute(rect, 'height');
      const x = numberAttribute(rect, 'x');
      const y = numberAttribute(rect, 'y');

      if (
        width !== 120 ||
        height !== 80 ||
        x === undefined ||
        y === undefined
      ) {
        return;
      }

      const task = rect.closest('[data-bpa-map-kind="task"]');
      const taskId = task?.getAttribute('data-bpa-map-activity-id');
      const node = addLegacyBpmnMapNode(
        nodes,
        { x: x + width / 2, y: y + height / 2 },
        'legacy-task',
        taskId,
      );

      normalizeLegacyTaskText(svg, rect, x, y, width, height);

      if (!task) {
        appendClassName(rect, 'bpa-map-task');
        rect.setAttribute('data-bpa-map-kind', 'task');
        rect.setAttribute('data-bpa-map-activity-id', node.id);
      }
    });

    Array.from(svg.querySelectorAll('polygon')).forEach((polygon) => {
      const center = centerFromPolygon(polygon);

      if (!center) {
        return;
      }

      const gateway = polygon.closest('[data-bpa-map-kind="gateway"]');
      const gatewayId =
        gateway?.getAttribute('data-bpa-map-activity-id') ??
        inferNodeIdFromEdges(center);
      const node = addLegacyBpmnMapNode(
        nodes,
        center,
        'legacy-node',
        gatewayId,
      );

      if (!gateway) {
        appendClassName(polygon, 'bpa-map-gateway');
        polygon.setAttribute('data-bpa-map-kind', 'gateway');
        polygon.setAttribute('data-bpa-map-activity-id', node.id);
      }
    });

    Array.from(svg.querySelectorAll('ellipse')).forEach((ellipse) => {
      const x = numberAttribute(ellipse, 'cx');
      const y = numberAttribute(ellipse, 'cy');

      if (x === undefined || y === undefined) {
        return;
      }

      addLegacyBpmnMapNode(nodes, { x, y }, 'legacy-node');
    });

    Array.from(svg.querySelectorAll('polyline[marker-end]')).forEach(
      (polyline, index) => {
        if (polyline.closest('[data-bpa-map-kind="edge"]')) {
          appendClassName(polyline, 'bpa-map-edge-line');
          return;
        }

        const points = pointsAttributeToPoints(
          polyline.getAttribute('points') ?? '',
        );
        const firstPoint = points[0];
        const lastPoint = points[points.length - 1];
        const parent = polyline.parentNode;

        if (!firstPoint || !lastPoint || !parent) {
          return;
        }

        const from = findLegacyBpmnMapNode(nodes, firstPoint);
        const to = findLegacyBpmnMapNode(nodes, lastPoint);

        if (!from || !to) {
          return;
        }

        const group = document.createElementNS(SVG_NAMESPACE, 'g');
        const hitPolyline = createBpmnMapEdgeHitShape(polyline);

        group.setAttribute('class', 'bpa-map-edge');
        group.setAttribute('data-bpa-map-kind', 'edge');
        group.setAttribute('data-bpa-map-edge-id', `legacy-edge-${index}`);
        group.setAttribute('data-bpa-map-from', from.id);
        group.setAttribute('data-bpa-map-to', to.id);

        appendClassName(polyline, 'bpa-map-edge-line');
        parent.insertBefore(group, polyline);
        group.appendChild(hitPolyline);
        group.appendChild(polyline);
      },
    );

    normalizeBpmnMapEdges(svg);
    markBpmnMapStickyLaneHeaders(svg);

    return new XMLSerializer().serializeToString(svg);
  } catch {
    return svgContent;
  }
}

function clearBpmnMapHover(root: Element | null) {
  root
    ?.querySelectorAll(
      '.bpa-map-hover, .bpa-map-hover--incoming, .bpa-map-hover--outgoing, .bpa-map-hover--recursive',
    )
    .forEach((element) => {
      element.classList.remove('bpa-map-hover');
      element.classList.remove('bpa-map-hover--incoming');
      element.classList.remove('bpa-map-hover--outgoing');
      element.classList.remove('bpa-map-hover--recursive');
    });
}

function clearBpmnMapSelectedTask(root: Element | null) {
  root
    ?.querySelectorAll('.bpa-map-selected')
    .forEach((element) => element.classList.remove('bpa-map-selected'));
}

function normalizeTaskLookupText(value?: string | null) {
  return normalizeBpmnMapText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function taskLookupVariants(value?: string | null) {
  const normalized = normalizeTaskLookupText(value);

  if (!normalized) {
    return [];
  }

  const variants = new Set([normalized]);
  const withoutOrdinalPrefix = normalized.replace(/^\d+[_\s.-]+/, '');

  if (withoutOrdinalPrefix) {
    variants.add(withoutOrdinalPrefix);
  }

  return Array.from(variants);
}

function resolveTaskDefinitionFromNodeOrder(
  tasks: TBPMGraphViewerTaskDefinition[],
  node: Element,
  root?: Element | null,
) {
  if (!root || tasks.length === 0) {
    return null;
  }

  const taskNode =
    node.getAttribute('data-bpa-map-kind') === 'task'
      ? node
      : node.closest('[data-bpa-map-kind="task"]');

  if (!taskNode || !root.contains(taskNode)) {
    return null;
  }

  const taskNodes = Array.from(
    root.querySelectorAll('[data-bpa-map-kind="task"]'),
  );

  if (taskNodes.length !== tasks.length) {
    return null;
  }

  const index = taskNodes.findIndex((candidate) => candidate === taskNode);

  return index >= 0 ? tasks[index] ?? null : null;
}

function chooseTaskDefinitionMatch(
  matches: TBPMGraphViewerTaskDefinition[],
  tasks: TBPMGraphViewerTaskDefinition[],
  node: Element,
  root?: Element | null,
) {
  if (matches.length === 0) {
    return null;
  }

  if (matches.length === 1) {
    return matches[0];
  }

  const orderedMatch = resolveTaskDefinitionFromNodeOrder(tasks, node, root);

  if (orderedMatch && matches.some((task) => task.id === orderedMatch.id)) {
    return orderedMatch;
  }

  return matches[0];
}

function resolveTaskDefinitionFromNode(
  tasks: TBPMGraphViewerTaskDefinition[],
  node: Element,
  root?: Element | null,
) {
  const candidateNodes = [
    node,
    node.parentElement?.closest('[data-bpa-map-kind="task"]') ?? undefined,
  ].filter((candidate): candidate is Element => Boolean(candidate));
  const candidates = candidateNodes
    .flatMap((candidateNode) => [
      candidateNode.getAttribute('data-bpa-map-task-name'),
      candidateNode.getAttribute('data-bpa-map-activity-id'),
      candidateNode.getAttribute('data-bpa-map-node-id'),
      candidateNode.getAttribute('id'),
      candidateNode.getAttribute('data-bpa-map-task-label'),
      getBpmnMapNodeText(candidateNode),
    ])
    .filter((candidate): candidate is string => Boolean(candidate?.trim()));

  for (const candidate of candidates) {
    const directMatch = chooseTaskDefinitionMatch(
      tasks.filter((task) =>
        [task.id, task.title, ...(task.aliases ?? [])].includes(candidate),
      ),
      tasks,
      node,
      root,
    );

    if (directMatch) {
      return directMatch;
    }
  }

  const normalizedCandidates = candidates.flatMap(taskLookupVariants);
  const normalizedMatch = chooseTaskDefinitionMatch(
    tasks.filter((task) => {
      const taskTexts = [task.id, task.title, ...(task.aliases ?? [])].flatMap(
        taskLookupVariants,
      );

      return normalizedCandidates.some((candidate) =>
        taskTexts.includes(candidate),
      );
    }),
    tasks,
    node,
    root,
  );

  if (normalizedMatch) {
    return normalizedMatch;
  }

  const orderedMatch = resolveTaskDefinitionFromNodeOrder(tasks, node, root);

  if (orderedMatch) {
    return orderedMatch;
  }

  if (tasks.length > 0) {
    return null;
  }

  const fallbackId = getBpmnMapNodeId(node);
  return fallbackId
    ? { id: fallbackId, title: getBpmnMapNodeText(node) || fallbackId }
    : null;
}

function applyBpmnMapSelectedTask(
  root: Element | null,
  tasks: TBPMGraphViewerTaskDefinition[],
  selectedTaskId: string | null,
) {
  clearBpmnMapSelectedTask(root);

  if (!root || !selectedTaskId) {
    return;
  }

  Array.from(root.querySelectorAll('[data-bpa-map-kind="task"]')).forEach(
    (task) => {
      if (
        resolveTaskDefinitionFromNode(tasks, task, root)?.id === selectedTaskId
      ) {
        task.classList.add('bpa-map-selected');
      }
    },
  );
}

function directBpmnMapTitle(element: Element) {
  return compactBpmnMapText(
    Array.from(element.childNodes).find(
      (node): node is Element =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node as Element).tagName.toLowerCase() === 'title',
    )?.textContent,
  );
}

function bpmnMapTruncatedLookupPrefix(value?: string | null) {
  return normalizeTaskLookupText(
    compactBpmnMapText(value).replace(/(?:\.{3}|…)+$/, ''),
  );
}

function resolveBpmnMapLaneTooltip(
  laneLabel: string,
  performerTooltips: string[],
) {
  const prefix = bpmnMapTruncatedLookupPrefix(laneLabel);

  if (!prefix) {
    return undefined;
  }

  const matches = performerTooltips.filter((tooltip) =>
    normalizeTaskLookupText(tooltip).startsWith(prefix),
  );

  return matches.length === 1 ? matches[0] : undefined;
}

function applyBpmnMapSemanticTooltips(
  root: Element | null,
  tasks: TBPMGraphViewerTaskDefinition[],
) {
  if (!root) {
    return;
  }

  Array.from(root.querySelectorAll('[data-bpa-map-kind="task"]')).forEach(
    (node) => {
      const task = resolveTaskDefinitionFromNode(tasks, node, root);
      const tooltip = task ? displayName(task.title, task.id) : undefined;

      if (!tooltip) {
        return;
      }

      setBpmnMapTooltip(node, tooltip);
      Array.from(node.querySelectorAll('rect, text, tspan')).forEach((shape) => {
        setBpmnMapTooltip(shape, tooltip);
      });
    },
  );

  const performerTooltips = Array.from(
    new Set(
      tasks
        .map((task) => compactBpmnMapText(task.performer))
        .filter(Boolean),
    ),
  );

  Array.from(
    root.querySelectorAll('[data-bpa-map-sticky-lane-kind="rect"]'),
  ).forEach((lane) => {
    const laneLabel =
      lane.getAttribute('data-bpa-map-lane-label') ||
      directBpmnMapTitle(lane);
    const tooltip = resolveBpmnMapLaneTooltip(laneLabel, performerTooltips);

    if (tooltip) {
      setBpmnMapTooltip(lane, tooltip);
      const originX = lane.getAttribute('data-bpa-map-sticky-origin-x');
      Array.from(
        root.querySelectorAll('[data-bpa-map-sticky-lane-kind="text"]'),
      )
        .filter(
          (text) =>
            text.getAttribute('data-bpa-map-sticky-origin-x') === originX,
        )
        .forEach((text) => {
          setBpmnMapTooltip(text, tooltip);
          Array.from(text.querySelectorAll('tspan')).forEach((part) =>
            setBpmnMapTooltip(part, tooltip),
          );
        });
    }
  });
}

function highlightBpmnMapEdge(
  root: Element,
  edge: Element,
  direction?: 'incoming' | 'outgoing',
) {
  const edgeId = edge.getAttribute('data-bpa-map-edge-id');
  const relatedLabels = edgeId
    ? Array.from(root.querySelectorAll(BPMN_MAP_EDGE_LABEL_SELECTOR)).filter(
        (label) => label.getAttribute('data-bpa-map-edge-id') === edgeId,
      )
    : [];

  edge.classList.add('bpa-map-hover');
  relatedLabels.forEach((label) => label.classList.add('bpa-map-hover'));

  if (direction) {
    edge.classList.add(`bpa-map-hover--${direction}`);
    relatedLabels.forEach((label) =>
      label.classList.add(`bpa-map-hover--${direction}`),
    );
  }
}

function bpmnMapTasksById(root: Element) {
  const tasksById = new Map<string, Element>();

  Array.from(root.querySelectorAll('[data-bpa-map-kind="task"]')).forEach(
    (task) => {
      const activityId = getBpmnMapNodeId(task);

      if (activityId) {
        tasksById.set(activityId, task);
      }
    },
  );

  return tasksById;
}

function bpmnMapJsonListFromAttribute(element: Element, attrName: string) {
  const value = element.getAttribute(attrName);

  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    return Array.isArray(parsed)
      ? parsed.filter((taskId): taskId is string => typeof taskId === 'string')
      : [];
  } catch {
    return value.split(/\s+/).filter(Boolean);
  }
}

function bpmnMapTaskIdsFromEdge(
  edge: Element,
  direction: 'incoming' | 'outgoing',
) {
  return bpmnMapJsonListFromAttribute(
    edge,
    `data-bpa-map-${direction}-task-ids`,
  );
}

function bpmnMapEdgeIdsFromEdge(
  edge: Element,
  direction: 'incoming' | 'outgoing',
) {
  return bpmnMapJsonListFromAttribute(
    edge,
    `data-bpa-map-${direction}-edge-ids`,
  );
}

function highlightBpmnMapEdgeConnectedTasks(root: Element, edge: Element) {
  const tasksById = bpmnMapTasksById(root);
  const edgesById = new Map<string, Element>();

  Array.from(root.querySelectorAll('[data-bpa-map-kind="edge"]')).forEach(
    (candidate) => {
      const edgeId = candidate.getAttribute('data-bpa-map-edge-id');

      if (edgeId) {
        edgesById.set(edgeId, candidate);
      }
    },
  );

  const highlightTask = (
    taskId: string,
    direction: 'incoming' | 'outgoing',
  ) => {
    const task = tasksById.get(taskId);

    if (!task) {
      return;
    }

    task.classList.add('bpa-map-hover');
    task.classList.add(`bpa-map-hover--${direction}`);
  };
  const highlightPathEdge = (edgeId: string) => {
    const pathEdge = edgesById.get(edgeId);

    if (pathEdge) {
      highlightBpmnMapEdge(root, pathEdge);
    }
  };

  bpmnMapTaskIdsFromEdge(edge, 'incoming').forEach((taskId) =>
    highlightTask(taskId, 'incoming'),
  );
  bpmnMapTaskIdsFromEdge(edge, 'outgoing').forEach((taskId) =>
    highlightTask(taskId, 'outgoing'),
  );
  bpmnMapEdgeIdsFromEdge(edge, 'incoming').forEach(highlightPathEdge);
  bpmnMapEdgeIdsFromEdge(edge, 'outgoing').forEach(highlightPathEdge);
}

function highlightBpmnMapTaskRecursivePaths(
  root: Element,
  activityId: string,
  direction: 'incoming' | 'outgoing',
) {
  const edges = Array.from(root.querySelectorAll('[data-bpa-map-kind="edge"]'));
  const tasksById = bpmnMapTasksById(root);
  const visitedNodes = new Set<string>();
  const visitedEdges = new Set<string>();

  const visit = (nodeId: string) => {
    if (!nodeId || visitedNodes.has(nodeId)) {
      return;
    }

    visitedNodes.add(nodeId);

    edges.forEach((edge) => {
      const sourceId =
        direction === 'outgoing'
          ? edge.getAttribute('data-bpa-map-from')
          : edge.getAttribute('data-bpa-map-to');

      if (sourceId !== nodeId) {
        return;
      }

      const edgeId = edge.getAttribute('data-bpa-map-edge-id');

      if (edgeId && visitedEdges.has(edgeId)) {
        return;
      }

      highlightBpmnMapEdge(root, edge, direction);

      if (edgeId) {
        visitedEdges.add(edgeId);
      }

      const nextId =
        direction === 'outgoing'
          ? edge.getAttribute('data-bpa-map-to')
          : edge.getAttribute('data-bpa-map-from');

      if (!nextId) {
        return;
      }

      const task = tasksById.get(nextId);

      if (task && nextId !== activityId) {
        task.classList.add('bpa-map-hover--recursive');
        task.classList.add(`bpa-map-hover--${direction}`);
      }

      visit(nextId);
    });
  };

  visit(activityId);
}

function highlightBpmnMapTaskEdges(root: Element, activityId: string) {
  const edges = Array.from(root.querySelectorAll('[data-bpa-map-kind="edge"]'));
  const tasksById = bpmnMapTasksById(root);
  const visitedOutgoing = new Set<string>();
  const visitedIncoming = new Set<string>();

  const highlightEdge = (edge: Element, direction: 'incoming' | 'outgoing') => {
    highlightBpmnMapEdge(root, edge, direction);
  };
  const highlightTask = (
    targetActivityId: string,
    direction: 'incoming' | 'outgoing',
  ) => {
    if (targetActivityId === activityId) {
      return;
    }

    tasksById
      .get(targetActivityId)
      ?.classList.add(`bpa-map-hover--${direction}`);
  };

  const visitOutgoing = (fromId: string) => {
    if (visitedOutgoing.has(fromId)) {
      return;
    }
    visitedOutgoing.add(fromId);
    edges.forEach((edge) => {
      if (edge.getAttribute('data-bpa-map-from') !== fromId) {
        return;
      }
      highlightEdge(edge, 'outgoing');
      const toId = edge.getAttribute('data-bpa-map-to');
      if (!toId) {
        return;
      }

      if (tasksById.has(toId)) {
        highlightTask(toId, 'outgoing');
      } else {
        visitOutgoing(toId);
      }
    });
  };

  const visitIncoming = (toId: string) => {
    if (visitedIncoming.has(toId)) {
      return;
    }
    visitedIncoming.add(toId);
    edges.forEach((edge) => {
      if (edge.getAttribute('data-bpa-map-to') !== toId) {
        return;
      }
      highlightEdge(edge, 'incoming');
      const fromId = edge.getAttribute('data-bpa-map-from');
      if (!fromId) {
        return;
      }

      if (tasksById.has(fromId)) {
        highlightTask(fromId, 'incoming');
      } else {
        visitIncoming(fromId);
      }
    });
  };

  visitOutgoing(activityId);
  visitIncoming(activityId);
}

function bpmnMapTaskHasPath(
  root: Element,
  activityId: string,
  direction: 'incoming' | 'outgoing',
) {
  const edges = Array.from(root.querySelectorAll('[data-bpa-map-kind="edge"]'));
  const tasksById = bpmnMapTasksById(root);
  const visited = new Set<string>();

  const visit = (nodeId: string): boolean => {
    if (!nodeId || visited.has(nodeId)) {
      return false;
    }

    visited.add(nodeId);

    return edges.some((edge) => {
      const sourceId =
        direction === 'outgoing'
          ? edge.getAttribute('data-bpa-map-from')
          : edge.getAttribute('data-bpa-map-to');

      if (sourceId !== nodeId) {
        return false;
      }

      const nextId =
        direction === 'outgoing'
          ? edge.getAttribute('data-bpa-map-to')
          : edge.getAttribute('data-bpa-map-from');

      return Boolean(nextId && (tasksById.has(nextId) || visit(nextId)));
    });
  };

  return visit(activityId);
}

function applyBpmnMapHoverTarget(
  root: Element,
  target: Element,
  modifiers: TBpmnMapHoverModifiers = {},
  taskEdgeHoverEnabled = true,
) {
  clearBpmnMapHover(root);

  const kind = target.getAttribute('data-bpa-map-kind');

  if (kind === 'edge') {
    highlightBpmnMapEdge(root, target);
    highlightBpmnMapEdgeConnectedTasks(root, target);
    return;
  }

  target.classList.add('bpa-map-hover');

  if (kind !== 'task' && kind !== 'gateway') {
    return;
  }

  const activityId = getBpmnMapNodeId(target);

  if (!activityId) {
    return;
  }

  if (kind === 'task' && !taskEdgeHoverEnabled) {
    return;
  }

  const isTaskRecursiveHover =
    kind === 'task' && (modifiers.ctrlKey || modifiers.shiftKey);

  if (isTaskRecursiveHover) {
    if (modifiers.ctrlKey) {
      target.classList.add('bpa-map-hover--recursive');
      target.classList.add('bpa-map-hover--outgoing');
      highlightBpmnMapTaskRecursivePaths(root, activityId, 'outgoing');
    }

    if (modifiers.shiftKey) {
      target.classList.add('bpa-map-hover--recursive');
      target.classList.add('bpa-map-hover--incoming');
      highlightBpmnMapTaskRecursivePaths(root, activityId, 'incoming');
    }

    return;
  }

  highlightBpmnMapTaskEdges(root, activityId);
}

function findBpmnMapHoverTarget(root: Element, element: Element) {
  const label = element.closest(BPMN_MAP_EDGE_LABEL_SELECTOR);

  if (label && root.contains(label)) {
    const containingEdge = label.closest('[data-bpa-map-kind="edge"]');

    if (containingEdge && root.contains(containingEdge)) {
      return containingEdge;
    }

    const edgeId = label.getAttribute('data-bpa-map-edge-id');
    if (edgeId) {
      const edge = Array.from(
        root.querySelectorAll('[data-bpa-map-kind="edge"]'),
      ).find(
        (candidate) =>
          candidate.getAttribute('data-bpa-map-edge-id') === edgeId,
      );

      if (edge) {
        return edge;
      }
    }
  }

  const edgeShape = element.closest('.bpa-map-edge-hit, .bpa-map-edge-line');

  if (edgeShape && root.contains(edgeShape)) {
    const containingEdge = edgeShape.closest('[data-bpa-map-kind="edge"]');

    if (containingEdge && root.contains(containingEdge)) {
      return containingEdge;
    }

    const edgeId = edgeShape.getAttribute('data-bpa-map-edge-id');

    if (edgeId) {
      const edge = Array.from(
        root.querySelectorAll('[data-bpa-map-kind="edge"]'),
      ).find(
        (candidate) =>
          candidate.getAttribute('data-bpa-map-edge-id') === edgeId,
      );

      if (edge) {
        return edge;
      }
    }
  }

  return element.closest('[data-bpa-map-kind]');
}

const BPMGraphTaskPreviewOverlay = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <Box
    className="bpmGraphViewer__taskPreviewOverlay"
    onClick={(ev) => ev.stopPropagation()}
    onPointerDown={(ev) => ev.stopPropagation()}
    onWheel={(ev) => ev.stopPropagation()}
  >
    <Box className="bpmGraphViewer__taskPreviewStack">{children}</Box>
  </Box>
);

const bpmGraphFieldPreviewType = (
  type: string,
): TBPMGraphViewerFormPreviewType => {
  switch ((type || '').toLowerCase()) {
    case 'button':
      return 'button';
    case 'check':
    case 'checkbox':
      return 'check';
    case 'combo':
    case 'select':
      return 'combo';
    case 'editor':
    case 'html':
    case 'richtext':
    case 'rich_text':
      return 'editor';
    case 'file':
      return 'file';
    case 'radio':
      return 'radio';
    case 'area':
    case 'textarea':
      return 'textarea';
    default:
      return 'input';
  }
};

const bpmGraphFormPreviewFieldGridColumn = (
  type: TBPMGraphViewerFormPreviewType,
  embedded?: boolean,
  tableCell?: boolean,
) => {
  if (tableCell) {
    return undefined;
  }
  if (embedded) {
    return '1 / -1';
  }
  return type === 'textarea' || type === 'editor' ? '1 / -1' : '2 / span 2';
};

const bpmGraphInputType = (
  field: TBPMGraphViewerTaskFormFieldDefinition,
  attribute?: TBPMGraphViewerAttributeDefinition,
) => {
  const fieldType = (field.type || '').toLowerCase();
  const attributeType = (attribute?.type || '').toLowerCase();

  switch (fieldType === 'input' || !fieldType ? attributeType : fieldType) {
    case 'date':
      return 'date';
    case 'integer':
    case 'number':
      return 'number';
    default:
      return 'text';
  }
};

const bpmGraphFormPreviewItems = (
  fields: TBPMGraphViewerTaskFormFieldDefinition[],
): TBPMGraphViewerFormPreviewItem[] => {
  const items: TBPMGraphViewerFormPreviewItem[] = [];
  const grids = new Map<
    string,
    Extract<TBPMGraphViewerFormPreviewItem, { type: 'grid' }>
  >();

  fields.forEach((field) => {
    if (!field.gridId) {
      items.push({ field, type: 'field' });
      return;
    }

    const current = grids.get(field.gridId);
    if (current) {
      current.fields.push(field);
      return;
    }

    const grid: Extract<TBPMGraphViewerFormPreviewItem, { type: 'grid' }> = {
      fields: [field],
      name: field.gridId,
      title: field.gridTitle || field.gridId,
      type: 'grid',
    };
    grids.set(field.gridId, grid);
    items.push(grid);
  });

  return items;
};

const bpmGraphFormPreviewOptions = (
  entities: TBPMGraphViewerEntityDefinition[],
  field: TBPMGraphViewerTaskFormFieldDefinition,
) => {
  const entity = entities.find((current) => current.name === field.entityId);
  if (!entity) {
    return [];
  }
  return sortByText(
    entity.options.map((option) => ({
      label: displayName(option.title, option.name),
      value: option.name,
    })),
    (option) => option.label,
  );
};

const bpmGraphFormPreviewAttribute = (
  attributes: TBPMGraphViewerAttributeDefinition[],
  field: TBPMGraphViewerTaskFormFieldDefinition,
) => attributes.find((attribute) => attribute.name === field.attributeId);

const bpmGraphFormPreviewFieldLabel = (
  attributes: TBPMGraphViewerAttributeDefinition[],
  field: TBPMGraphViewerTaskFormFieldDefinition,
) =>
  bpmGraphFormPreviewAttribute(attributes, field)?.title ||
  field.title ||
  field.attributeId ||
  '-';

const BPMGraphFormPreviewRequiredIndicator: React.FC<{
  className?: string;
  required?: boolean;
}> = ({ className = '', required }) => {
  const labels = useBPMGraphViewerLabels();

  return required ? (
    <Box
      as="abbr"
      className={`required formPreview__requiredIndicator ${className}`.trim()}
      title={labels.required}
    >
      *
    </Box>
  ) : null;
};

const BPMGraphFormPreviewFieldTitle: React.FC<{
  avoidColon?: boolean;
  label: string;
  required?: boolean;
}> = ({ avoidColon, label, required }) => {
  const requiredBefore =
    (window as TApiaThemeWindow).SHOW_REQUIRED_POSITION !== '0';

  return (
    <Box as="span" className="field__title">
      {requiredBefore && (
        <BPMGraphFormPreviewRequiredIndicator
          className="before"
          required={required}
        />
      )}
      <Box as="span" className="formPreview__fieldTitleText">
        {label}
        {label && !avoidColon ? ':' : ''}
      </Box>
      {!requiredBefore && (
        <BPMGraphFormPreviewRequiredIndicator required={required} />
      )}
    </Box>
  );
};

const BPMGraphFormPreviewFieldWrapper: React.FC<{
  children: React.ReactNode;
  label: string;
  required?: boolean;
}> = ({ children, label, required }) => (
  <Box as="label" className="field__label" title={label}>
    <Box className="field__title_container">
      <BPMGraphFormPreviewFieldTitle label={label} required={required} />
    </Box>
    <Box className="field__control_container">{children}</Box>
  </Box>
);

const BPMGraphFormPreviewStaticFieldWrapper: React.FC<{
  children: React.ReactNode;
  label: string;
  required?: boolean;
}> = ({ children, label, required }) => (
  <Box className="field__label" title={label}>
    <Box className="field__title_container">
      <BPMGraphFormPreviewFieldTitle label={label} required={required} />
    </Box>
    <Box className="field__control_container">{children}</Box>
  </Box>
);

const preventBPMGraphFormPreviewFileAction = (
  event: React.SyntheticEvent,
) => {
  event.preventDefault();
};

const BPMGraphFormPreviewApiaFileDrop: React.FC<{
  compact?: boolean;
  label: string;
  readonly?: boolean;
}> = ({ compact, label, readonly }) => {
  const labels = useBPMGraphViewerLabels();

  return (
    <Box
      className={`dropzone__container formPreview__fileDropzone ${
        compact ? 'formPreview__fileDrop--compact' : ''
      } ${readonly ? 'formPreview__fileDropzone--readonly' : ''}`}
    >
      <button
        className="dropzone formPreview__fileDropzoneTarget"
        disabled={readonly}
        onClick={preventBPMGraphFormPreviewFileAction}
        onKeyDown={preventBPMGraphFormPreviewFileAction}
        tabIndex={readonly ? -1 : 0}
        type="button"
      >
        {labels.uploadFile}
      </button>
      <IconButton
        className="formPreview__fileInfoButton bpmGraphViewer__controlButton"
        iconSize="Sm"
        icon="Info"
        onClick={preventBPMGraphFormPreviewFileAction}
        size="Lg"
        title={`${labels.fileInfo}: ${label}`}
        variant="icon"
      />
    </Box>
  );
};

const BPMGraphFormPreviewField: React.FC<{
  attributes: TBPMGraphViewerAttributeDefinition[];
  entities: TBPMGraphViewerEntityDefinition[];
  embedded?: boolean;
  field: TBPMGraphViewerTaskFormFieldDefinition;
  forceReadonly?: boolean;
  tableCell?: boolean;
}> = ({ attributes, embedded, entities, field, forceReadonly, tableCell }) => {
  if (field.hidden) {
    return null;
  }

  const type = bpmGraphFieldPreviewType(field.type);
  const readonly = Boolean(forceReadonly || field.readonly || field.disabled);
  const attribute = bpmGraphFormPreviewAttribute(attributes, field);
  const options = bpmGraphFormPreviewOptions(entities, field);
  const label =
    attribute?.title || field.title || field.attributeId || '-';
  const inputType = bpmGraphInputType(field, attribute);
  const executionFieldType = FORM_PREVIEW_FIELD_STYLE_MAP[type];
  const gridColumn = bpmGraphFormPreviewFieldGridColumn(
    type,
    embedded,
    tableCell,
  );
  let control: React.ReactNode;
  const wrapFieldControl = (children: React.ReactNode) =>
    tableCell ? (
      children
    ) : (
      <BPMGraphFormPreviewFieldWrapper
        label={label}
        required={field.required}
      >
        {children}
      </BPMGraphFormPreviewFieldWrapper>
    );

  if (type === 'combo') {
    control = wrapFieldControl(
      <Box
        className="formPreview__inputHost"
        sx={{ select: { variant: 'forms.select' } }}
      >
        <select
          aria-label={label}
          defaultValue=""
          disabled={readonly}
          tabIndex={-1}
        >
          <option value="" />
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Box>,
    );
  } else if (type === 'textarea' || type === 'editor') {
    control = wrapFieldControl(
      <Box
        className="formPreview__inputHost formPreview__inputHost--textarea"
        sx={{ textarea: { variant: 'forms.textarea' } }}
      >
        <textarea
          aria-label={label}
          readOnly={readonly}
          rows={type === 'editor' && !tableCell ? 6 : tableCell ? 2 : 4}
          tabIndex={-1}
        />
      </Box>,
    );
  } else if (type === 'check') {
    control = (
      <>
        <div className="fake__label" />
        <Box className="label" title={label}>
          <Checkbox
            defaultChecked={false}
            disabled={readonly}
            readOnly={readonly}
          />
          <BPMGraphFormPreviewFieldTitle
            avoidColon
            label={label}
            required={field.required}
          />
        </Box>
      </>
    );
  } else if (type === 'radio') {
    const displayOptions =
      options.length > 0
        ? options
        : [
            { label: 'Opcion 1', value: 'option_1' },
            { label: 'Opcion 2', value: 'option_2' },
          ];
    control = (
      <Box aria-label={label} className="fieldset" role="group">
        <Box className="radio__layout__wrapper">
          <Box as="legend" className="radio_label">
            <BPMGraphFormPreviewFieldTitle
              label={label}
              required={field.required}
            />
          </Box>
          <Box className="option__container options_wrapper">
            {displayOptions.map((option, index) => (
              <Box
                as="label"
                className="radio"
                key={`${option.value}_${index}`}
              >
                <input
                  aria-label={`${label} ${index + 1}`}
                  disabled={readonly}
                  name={`preview_${field.attributeId}`}
                  type="radio"
                  value={option.value}
                />
                <Box className="radio__label formPreview__optionLabel">
                  {option.label}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  } else if (type === 'file') {
    control = tableCell ? (
      <BPMGraphFormPreviewApiaFileDrop
        compact={tableCell}
        label={label}
        readonly={readonly}
      />
    ) : (
      <BPMGraphFormPreviewStaticFieldWrapper
        label={label}
        required={field.required}
      >
        <BPMGraphFormPreviewApiaFileDrop
          label={label}
          readonly={readonly}
        />
      </BPMGraphFormPreviewStaticFieldWrapper>
    );
  } else if (type === 'button') {
    control = (
      <>
        <div className="fake__label" />
        <Box className="formPreview__buttonHost">
          <SimpleButton
            disabled={readonly}
            title={label}
            type="button"
            variant="outline"
          >
            {label}
          </SimpleButton>
        </Box>
      </>
    );
  } else {
    control = wrapFieldControl(
      <Box
        className="formPreview__inputHost"
        sx={{ input: { variant: 'forms.input' } }}
      >
        {inputType === 'date' ? (
          <DateInput
            className="formPreview__dateInput"
            disabled={readonly}
            props={{
              'aria-label': label,
              tabIndex: -1,
            }}
            readonly={readonly}
          />
        ) : (
          <input
            aria-label={label}
            readOnly={readonly}
            tabIndex={-1}
            type={inputType}
          />
        )}
      </Box>,
    );
  }

  return (
    <Box
      className={`apia__field field__${executionFieldType} ${
        tableCell ? 'formPreview__gridCellControl' : 'formPreview__field'
      } ${readonly ? 'formPreview__field--readonly' : ''}`}
      data-att-id={field.attributeId || undefined}
      data-field-id={field.attributeId}
      data-name={field.attributeId}
      data-type={executionFieldType}
      sx={
        tableCell
          ? undefined
          : {
              gridColumn: responsive({
                0: '1 / -1',
                3: gridColumn,
              }),
            }
      }
      {...getVariant(`layout.execution.form.fields.${executionFieldType}`)}
    >
      {control}
    </Box>
  );
};

const BPMGraphFormPreviewGrid: React.FC<{
  attributes: TBPMGraphViewerAttributeDefinition[];
  entities: TBPMGraphViewerEntityDefinition[];
  forceReadonly?: boolean;
  grid: Extract<TBPMGraphViewerFormPreviewItem, { type: 'grid' }>;
}> = ({ attributes, entities, forceReadonly, grid }) => {
  const [maximized, setMaximized] = React.useState(false);
  const labels = useBPMGraphViewerLabels();
  const tableMinWidth = Math.max(grid.fields.length, 1) * 150;
  const closeMaximized = React.useCallback(() => setMaximized(false), []);
  const toggleMaximized = React.useCallback((ev: React.MouseEvent) => {
    ev.preventDefault();
    setMaximized((current) => !current);
  }, []);
  const noopGridAction = React.useCallback((ev: React.MouseEvent) => {
    ev.preventDefault();
  }, []);
  const content = (isOverlay: boolean) => (
    <>
      <Box className="formPreview__gridTitle" title={grid.title}>
        {grid.title}
      </Box>
      <Box className="formPreview__gridTableScroller">
        <Box
          as="table"
          className="formPreview__gridTable"
          style={{ minWidth: tableMinWidth }}
          {...getVariant('layout.common.tables.secondary')}
        >
          <thead>
            <tr>
              {grid.fields.map((field) => (
                <th
                  key={field.attributeId}
                  title={bpmGraphFormPreviewFieldLabel(attributes, field)}
                >
                  {bpmGraphFormPreviewFieldLabel(attributes, field)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {grid.fields.map((field) => (
                <td key={field.attributeId}>
                  <BPMGraphFormPreviewField
                    attributes={attributes}
                    entities={entities}
                    field={field}
                    forceReadonly={forceReadonly}
                    tableCell
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </Box>
      </Box>
      <Box className="formPreview__gridFooter">
        <span className="formPreview__gridFooterStart">
          <IconButton
            className="formPreview__gridFooterButton bpmGraphViewer__controlButton"
            iconSize="Sm"
            icon={isOverlay ? 'Minimize' : 'Maximize'}
            onClick={isOverlay ? closeMaximized : toggleMaximized}
            size="Lg"
            title={isOverlay ? labels.restoreGrid : labels.maximizeGrid}
            variant="icon"
          />
        </span>
        <span className="formPreview__gridFooterActions">
          <IconButton
            className="formPreview__gridFooterButton bpmGraphViewer__controlButton"
            disabled={forceReadonly}
            iconSize="Sm"
            icon="Plus"
            onClick={noopGridAction}
            size="Lg"
            title={labels.addGridRow}
            variant="icon"
          />
          <IconButton
            className="formPreview__gridFooterButton bpmGraphViewer__controlButton"
            disabled={forceReadonly}
            iconSize="Sm"
            icon="Trash"
            onClick={noopGridAction}
            size="Lg"
            title={labels.deleteGridRow}
            variant="icon"
          />
        </span>
      </Box>
    </>
  );

  React.useEffect(() => {
    if (!maximized) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMaximized(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [maximized]);

  return (
    <>
      <Box
        className="formPreview__grid"
        sx={{
          gridColumn: responsive({
            0: '1 / -1',
            3: '1 / -1',
          }),
        }}
      >
        {content(false)}
      </Box>
      {maximized && (
        <Box
          className="bpmGraphViewer__gridModal"
          role="dialog"
          aria-label={grid.title}
          onClick={closeMaximized}
        >
          <Box
            className="bpmGraphViewer__gridModalContent formPreview__grid"
            onClick={(ev) => ev.stopPropagation()}
          >
            {content(true)}
          </Box>
        </Box>
      )}
    </>
  );
};

const BPMGraphFormPreviewAccordionStateBridge: React.FC<{
  stateKey: string;
}> = ({ stateKey }) => {
  const accordion = useAccordionContext();

  accordion.hooks.useItemStateUpdate(
    React.useCallback(
      (state) => {
        bpmGraphFormPreviewCollapsedByStateKey.set(
          stateKey,
          !state.isExpanded,
        );
      },
      [stateKey],
    ),
  );

  return null;
};

const BPMGraphFormResultPreview = React.memo(({
  attributes,
  embedded,
  entities,
  forceReadonly,
  form,
  stateKey,
}: {
  attributes: TBPMGraphViewerAttributeDefinition[];
  embedded?: boolean;
  entities: TBPMGraphViewerEntityDefinition[];
  forceReadonly?: boolean;
  form: TBPMGraphViewerTaskFormDefinition;
  stateKey?: string;
}) => {
  const visibleFields = React.useMemo(
    () => form.fields.filter((field) => !field.hidden),
    [form.fields],
  );
  const items = React.useMemo(
    () => bpmGraphFormPreviewItems(visibleFields),
    [visibleFields],
  );
  const title = React.useMemo(
    () => displayName(form.title, form.name),
    [form.name, form.title],
  );
  const formKey = form.name || title;
  const accordionStateKey = stateKey || formKey;
  const defaultExpanded =
    !bpmGraphFormPreviewCollapsedByStateKey.get(accordionStateKey);
  const labels = useBPMGraphViewerLabels();
  const isReadonlyForm = Boolean(forceReadonly);

  return (
    <Box
      className={`formPreview__viewport bpmGraphFormPreview ${
        embedded ? 'bpmGraphFormPreview--embedded' : ''
      } ${isReadonlyForm ? 'bpmGraphFormPreview--readonly' : ''}`}
    >
      <Accordion
        className="formPreview__accordion"
        key={`formPreviewAccordion_${accordionStateKey}`}
        singleExpand
        variant="layout.common.components.accordion.primary"
      >
        <AccordionItem
          buttonProps={{
            ariaLabel: isReadonlyForm
              ? `${title} - ${labels.readonlyForm}`
              : title,
            label: title,
            title,
          }}
          defaultExpanded={defaultExpanded}
          id={`accordion_E_${formKey}`}
        >
          <BPMGraphFormPreviewAccordionStateBridge
            stateKey={accordionStateKey}
          />
          <Box
            as="form"
            className="formPreview__form"
            aria-readonly={isReadonlyForm || undefined}
            data-form-name={form.name}
            data-readonly={isReadonlyForm || undefined}
            onSubmit={(ev) => ev.preventDefault()}
            sx={{
              display: 'grid',
              gridTemplateColumns: responsive({
                0: 'minmax(0, 1fr)',
                3: embedded
                  ? 'minmax(0, 1fr)'
                  : `repeat(${FORM_PREVIEW_COLUMNS}, minmax(0, 1fr))`,
              }),
            }}
            {...getVariant('layout.execution.form')}
          >
            {items.length === 0 ? (
              <Box
                className="formPreview__empty"
                sx={{ gridColumn: '1 / -1' }}
              >
                {labels.noVisibleFields}
              </Box>
            ) : (
              items.map((item, index) =>
                item.type === 'grid' ? (
                  <BPMGraphFormPreviewGrid
                    key={`${item.name}_${index}`}
                    attributes={attributes}
                    entities={entities}
                    forceReadonly={forceReadonly}
                    grid={item}
                  />
                ) : (
                  <BPMGraphFormPreviewField
                    key={`${item.field.attributeId}_${index}`}
                    attributes={attributes}
                    embedded={embedded}
                    entities={entities}
                    field={item.field}
                    forceReadonly={forceReadonly}
                  />
                ),
              )
            )}
          </Box>
        </AccordionItem>
      </Accordion>
    </Box>
  );
});
BPMGraphFormResultPreview.displayName = 'BPMGraphFormResultPreview';

export const BPMGraphFormPreview = React.memo(
  ({
    attributes = [],
    className,
    embedded,
    entities = [],
    forceReadonly,
    form,
    labels: userLabels,
    stateKey,
  }: TBPMGraphFormPreviewProps) => {
    const labels = React.useMemo(
      () => ({ ...DEFAULT_BPM_GRAPH_VIEWER_LABELS, ...userLabels }),
      [userLabels],
    );

    return (
      <BPMGraphViewerLabelsContext.Provider value={labels}>
        <Box
          className={`bpmGraphViewer ${className ?? ''}`.trim()}
          {...getVariant('layout.common.components.bpmGraphViewer')}
        >
          <BPMGraphFormResultPreview
            attributes={attributes}
            embedded={embedded}
            entities={entities}
            forceReadonly={forceReadonly}
            form={form}
            stateKey={stateKey}
          />
        </Box>
      </BPMGraphViewerLabelsContext.Provider>
    );
  },
);

BPMGraphFormPreview.displayName = 'BPMGraphFormPreview';

const BPMGraphDefaultTaskPreview = React.memo(({
  attributes,
  empty,
  entities,
  formsByName,
  task,
}: {
  attributes: TBPMGraphViewerAttributeDefinition[];
  empty?: React.ReactNode;
  entities: TBPMGraphViewerEntityDefinition[];
  formsByName: Map<string, TBPMGraphViewerTaskFormDefinition>;
  task: TBPMGraphViewerTaskDefinition;
}) => {
  const formNames = task.forms ?? [];
  const labels = useBPMGraphViewerLabels();

  if (formNames.length === 0) {
    return (
      empty ?? (
        <Box className="bpmGraphViewer__taskPreviewEmpty">
          {labels.noTaskForms}
        </Box>
      )
    );
  }

  return (
    <>
      {formNames.map((formName, index) => {
        const form = formsByName.get(formName) ?? {
          fields: [],
          name: formName,
          title: formName,
        };

        return (
          <BPMGraphFormResultPreview
            key={`${formName}-${form.name}-${index}`}
            attributes={attributes}
            embedded
            entities={entities}
            form={form}
          />
        );
      })}
    </>
  );
});
BPMGraphDefaultTaskPreview.displayName = 'BPMGraphDefaultTaskPreview';

const BPMGraphSelectedTaskPreview = React.memo(
  ({
    attributes,
    entities,
    formsByName,
    renderTaskPreview,
    selectedTask,
    taskPreviewEmpty,
  }: {
    attributes: TBPMGraphViewerAttributeDefinition[];
    entities: TBPMGraphViewerEntityDefinition[];
    formsByName: Map<string, TBPMGraphViewerTaskFormDefinition>;
    renderTaskPreview?: TBPMGraphViewerProps['renderTaskPreview'];
    selectedTask: TBPMGraphViewerTaskDefinition | null;
    taskPreviewEmpty?: React.ReactNode;
  }) => {
    if (!selectedTask) {
      return null;
    }

    const selectedTaskPreview = renderTaskPreview ? (
      renderTaskPreview(selectedTask)
    ) : (
      <BPMGraphDefaultTaskPreview
        attributes={attributes}
        empty={taskPreviewEmpty}
        entities={entities}
        formsByName={formsByName}
        task={selectedTask}
      />
    );

    return (
      <BPMGraphTaskPreviewOverlay>
        {selectedTaskPreview}
      </BPMGraphTaskPreviewOverlay>
    );
  },
);
BPMGraphSelectedTaskPreview.displayName = 'BPMGraphSelectedTaskPreview';

const BPMGraphMapContent = React.memo(
  React.forwardRef<
    HTMLDivElement,
    {
      alt: string;
      className: string;
      imageSrc?: string;
      normalizedSvgContent?: string;
      style: React.CSSProperties;
    }
  >(({ alt, className, imageSrc, normalizedSvgContent, style }, ref) =>
    normalizedSvgContent ? (
      <Box
        aria-label={alt}
        className={`${className} bpmGraphViewer__inlineSvg`}
        ref={ref}
        role="img"
        style={style}
        dangerouslySetInnerHTML={{ __html: normalizedSvgContent }}
      />
    ) : imageSrc ? (
      <img alt={alt} className={className} src={imageSrc} style={style} />
    ) : null,
  ),
);
BPMGraphMapContent.displayName = 'BPMGraphMapContent';

const BPMGraphImageViewport = ({
  alt,
  attributes,
  entities,
  forms,
  imageSrc,
  renderTaskPreview,
  svgContent,
  taskEdgeHoverEnabled,
  taskPreviewEnabled,
  taskPreviewEmpty,
  tasks,
}: Required<
  Pick<TBPMGraphViewerProps, 'alt' | 'attributes' | 'entities' | 'tasks'>
> &
  Pick<
    TBPMGraphViewerProps,
    | 'forms'
    | 'imageSrc'
    | 'renderTaskPreview'
    | 'svgContent'
    | 'taskEdgeHoverEnabled'
    | 'taskPreviewEnabled'
    | 'taskPreviewEmpty'
  >) => {
  const [isPanning, setIsPanning] = React.useState(false);
  const labels = useBPMGraphViewerLabels();
  const [isMapViewportAnimating, setIsMapViewportAnimating] =
    React.useState(false);
  const [selectedTaskId, setSelectedTaskId] = React.useState<string | null>(
    null,
  );
  const inlineSvgRef = React.useRef<HTMLDivElement>(null);
  const mapViewportAnimationTimeoutRef = React.useRef<number | null>(null);
  const clickSelectionTimeoutRef = React.useRef<number | null>(null);
  const hoveredBpmnMapTargetRef = React.useRef<Element | null>(null);
  const bpmnMapHoverModifiersRef = React.useRef<TBpmnMapHoverModifiers>({});
  const suppressNextMapClickRef = React.useRef(false);
  const [imageViewport, setImageViewport] =
    React.useState<TMapViewport>(DEFAULT_MAP_VIEWPORT);
  const effectiveSvgContent = React.useMemo(
    () => svgContent ?? svgContentFromImageSrc(imageSrc),
    [imageSrc, svgContent],
  );
  const normalizedSvgContent = React.useMemo(
    () =>
      effectiveSvgContent
        ? normalizeLegacyBpmnMapSvg(effectiveSvgContent)
        : undefined,
    [effectiveSvgContent],
  );
  const tasksById = React.useMemo(
    () => new Map(tasks.map((task) => [task.id, task])),
    [tasks],
  );
  const formsByName = React.useMemo(
    () =>
      new Map(
        (forms ?? []).flatMap((form) =>
          bpmGraphFormLookupKeys(form).map((key) => [key, form] as const),
        ),
      ),
    [forms],
  );
  const selectedTask = React.useMemo(
    () => (selectedTaskId ? tasksById.get(selectedTaskId) ?? null : null),
    [selectedTaskId, tasksById],
  );

  const cancelPendingClickSelection = React.useCallback(() => {
    if (clickSelectionTimeoutRef.current !== null) {
      window.clearTimeout(clickSelectionTimeoutRef.current);
      clickSelectionTimeoutRef.current = null;
    }
  }, []);

  const updateBpmnMapLegend = React.useCallback(
    (
      root: Element | null,
      target?: Element | null,
      options: { taskEdgeHoverEnabled?: boolean } = {},
    ) => {
      const viewport = root?.closest('.bpmGraphViewer__viewport');
      let hasOutgoingHover = Boolean(
        root?.querySelector('.bpa-map-hover--outgoing'),
      );
      let hasIncomingHover = Boolean(
        root?.querySelector('.bpa-map-hover--incoming'),
      );

      if (
        root &&
        target &&
        options.taskEdgeHoverEnabled !== false &&
        target.getAttribute('data-bpa-map-kind') === 'task'
      ) {
        const activityId = getBpmnMapNodeId(target);

        if (activityId) {
          hasOutgoingHover =
            hasOutgoingHover ||
            bpmnMapTaskHasPath(root, activityId, 'outgoing');
          hasIncomingHover =
            hasIncomingHover ||
            bpmnMapTaskHasPath(root, activityId, 'incoming');
        }
      }

      viewport?.classList.toggle(
        'bpmGraphViewer__viewport--hasOutgoingHover',
        hasOutgoingHover,
      );
      viewport?.classList.toggle(
        'bpmGraphViewer__viewport--hasIncomingHover',
        hasIncomingHover,
      );
    },
    [],
  );

  const clearBpmnMapHoverState = React.useCallback(
    (root: Element | null) => {
      clearBpmnMapHover(root);
      updateBpmnMapLegend(root);
    },
    [updateBpmnMapLegend],
  );

  const cancelMapViewportAnimation = React.useCallback(() => {
    if (mapViewportAnimationTimeoutRef.current !== null) {
      window.clearTimeout(mapViewportAnimationTimeoutRef.current);
      mapViewportAnimationTimeoutRef.current = null;
    }

    setIsMapViewportAnimating(false);
  }, []);

  const animateMapViewportChange = React.useCallback(() => {
    if (mapViewportAnimationTimeoutRef.current !== null) {
      window.clearTimeout(mapViewportAnimationTimeoutRef.current);
    }

    setIsMapViewportAnimating(true);
    mapViewportAnimationTimeoutRef.current = window.setTimeout(() => {
      setIsMapViewportAnimating(false);
      mapViewportAnimationTimeoutRef.current = null;
    }, BPMN_TASK_FOCUS_ANIMATION_MS);
  }, []);

  React.useEffect(
    () => () => {
      cancelPendingClickSelection();
      if (mapViewportAnimationTimeoutRef.current !== null) {
        window.clearTimeout(mapViewportAnimationTimeoutRef.current);
      }
    },
    [cancelPendingClickSelection],
  );

  React.useEffect(() => {
    cancelMapViewportAnimation();
    cancelPendingClickSelection();
    setImageViewport(DEFAULT_MAP_VIEWPORT);
    setSelectedTaskId(null);
    hoveredBpmnMapTargetRef.current = null;
    bpmnMapHoverModifiersRef.current = {};
    clearBpmnMapHoverState(inlineSvgRef.current);
    clearBpmnMapSelectedTask(inlineSvgRef.current);
  }, [
    cancelMapViewportAnimation,
    cancelPendingClickSelection,
    clearBpmnMapHoverState,
    imageSrc,
    normalizedSvgContent,
  ]);

  React.useEffect(() => {
    applyBpmnMapSelectedTask(inlineSvgRef.current, tasks, selectedTaskId);
    applyBpmnMapSemanticTooltips(inlineSvgRef.current, tasks);
  }, [normalizedSvgContent, selectedTaskId, tasks]);

  React.useEffect(() => {
    updateStickyBpmnMapLanes(inlineSvgRef.current);
  }, [imageViewport, normalizedSvgContent]);

  const imageStyle = React.useMemo(
    () =>
      ({
        height: `${imageViewport.scale * 100}%`,
        left: `${imageViewport.x}px`,
        top: `${imageViewport.y}px`,
        width: `${imageViewport.scale * 100}%`,
      }) as React.CSSProperties,
    [imageViewport],
  );

  const imageContentClassName = `bpmGraphViewer__imageContent ${
    isMapViewportAnimating ? 'bpmGraphViewer__imageContent--animating' : ''
  }`.trim();

  const resetZoom = React.useCallback(() => {
    cancelMapViewportAnimation();
    setImageViewport(DEFAULT_MAP_VIEWPORT);
  }, [cancelMapViewportAnimation]);

  const applyBpmnMapHover = React.useCallback(
    (target: Element | null, modifiers: TBpmnMapHoverModifiers = {}) => {
      const root = inlineSvgRef.current;

      if (!root || !normalizedSvgContent || !target || !root.contains(target)) {
        clearBpmnMapHoverState(root);
        return;
      }

      applyBpmnMapHoverTarget(
        root,
        target,
        modifiers,
        taskEdgeHoverEnabled,
      );
      updateBpmnMapLegend(root, target, { taskEdgeHoverEnabled });
    },
    [
      clearBpmnMapHoverState,
      normalizedSvgContent,
      taskEdgeHoverEnabled,
      updateBpmnMapLegend,
    ],
  );

  React.useEffect(() => {
    const refreshModifierHover = (modifiers: TBpmnMapHoverModifiers) => {
      const current = bpmnMapHoverModifiersRef.current;

      if (
        Boolean(current.ctrlKey) === Boolean(modifiers.ctrlKey) &&
        Boolean(current.shiftKey) === Boolean(modifiers.shiftKey)
      ) {
        return;
      }

      bpmnMapHoverModifiersRef.current = modifiers;
      applyBpmnMapHover(hoveredBpmnMapTargetRef.current, modifiers);
    };

    const onModifierKeyChange = (event: KeyboardEvent) => {
      refreshModifierHover({
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
      });
    };

    const onWindowBlur = () => {
      refreshModifierHover({});
    };

    window.addEventListener('keydown', onModifierKeyChange);
    window.addEventListener('keyup', onModifierKeyChange);
    window.addEventListener('blur', onWindowBlur);

    return () => {
      window.removeEventListener('keydown', onModifierKeyChange);
      window.removeEventListener('keyup', onModifierKeyChange);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [applyBpmnMapHover]);

  const handleBpmnMapHover = React.useCallback(
    (ev: React.PointerEvent) => {
      const root = inlineSvgRef.current;

      if (!root || !normalizedSvgContent || isPanning) {
        return;
      }

      const target =
        ev.target instanceof Element
          ? findBpmnMapHoverTarget(root, ev.target)
          : null;

      if (!target || !root.contains(target)) {
        hoveredBpmnMapTargetRef.current = null;
        clearBpmnMapHoverState(root);
        return;
      }

      const modifiers = {
        ctrlKey: ev.ctrlKey,
        shiftKey: ev.shiftKey,
      };

      hoveredBpmnMapTargetRef.current = target;
      bpmnMapHoverModifiersRef.current = modifiers;
      applyBpmnMapHover(target, modifiers);
    },
    [
      applyBpmnMapHover,
      clearBpmnMapHoverState,
      isPanning,
      normalizedSvgContent,
    ],
  );

  const handleBpmnMapPointerLeave = React.useCallback(() => {
    hoveredBpmnMapTargetRef.current = null;
    clearBpmnMapHoverState(inlineSvgRef.current);
  }, [clearBpmnMapHoverState]);

  const handleMapWheel = React.useCallback(
    (ev: React.WheelEvent) => {
      ev.preventDefault();
      cancelMapViewportAnimation();

      const rect = ev.currentTarget.getBoundingClientRect();
      const pointerX = ev.clientX - rect.left - rect.width / 2;
      const pointerY = ev.clientY - rect.top - rect.height / 2;

      setImageViewport((current) => {
        const nextScale = clampNumber(
          current.scale * Math.exp(-ev.deltaY * WHEEL_ZOOM_SENSITIVITY),
          MIN_MAP_SCALE,
          MAX_MAP_SCALE,
        );
        const ratio = nextScale / current.scale;

        return {
          scale: nextScale,
          x: pointerX - (pointerX - current.x) * ratio,
          y: pointerY - (pointerY - current.y) * ratio,
        };
      });
    },
    [cancelMapViewportAnimation],
  );

  const handleMapDoubleClick = React.useCallback(
    (ev: React.MouseEvent) => {
      cancelPendingClickSelection();
      const root = inlineSvgRef.current;

      if (!root || !normalizedSvgContent || !(ev.target instanceof Element)) {
        return;
      }

      const target = findBpmnMapHoverTarget(root, ev.target);

      if (
        !target ||
        !root.contains(target) ||
        target.getAttribute('data-bpa-map-kind') !== 'task'
      ) {
        return;
      }

      const containerRect = ev.currentTarget.getBoundingClientRect();
      const taskRect = target.getBoundingClientRect();

      if (
        containerRect.width <= 0 ||
        containerRect.height <= 0 ||
        taskRect.width <= 0 ||
        taskRect.height <= 0
      ) {
        return;
      }

      ev.preventDefault();
      ev.stopPropagation();
      animateMapViewportChange();
      setIsPanning(false);

      const containerCenterX = containerRect.left + containerRect.width / 2;
      const containerCenterY = containerRect.top + containerRect.height / 2;
      const taskCenterX = taskRect.left + taskRect.width / 2 - containerCenterX;
      const taskCenterY = taskRect.top + taskRect.height / 2 - containerCenterY;
      const desiredTaskWidth =
        containerRect.width * BPMN_TASK_FOCUS_WIDTH_RATIO;

      setImageViewport((current) => {
        const nextScale = clampNumber(
          current.scale * (desiredTaskWidth / taskRect.width),
          MIN_MAP_SCALE,
          MAX_MAP_SCALE,
        );
        const ratio = nextScale / current.scale;

        return {
          scale: nextScale,
          x: -(taskCenterX - current.x) * ratio,
          y: -(taskCenterY - current.y) * ratio,
        };
      });
    },
    [
      animateMapViewportChange,
      cancelPendingClickSelection,
      normalizedSvgContent,
    ],
  );

  const resolveTaskFromMapEventTarget = React.useCallback(
    (eventTarget: EventTarget | null) => {
      const root = inlineSvgRef.current;

      if (!root || !normalizedSvgContent || !(eventTarget instanceof Element)) {
        return null;
      }

      const target = findBpmnMapHoverTarget(root, eventTarget);

      if (
        target &&
        root.contains(target) &&
        target.getAttribute('data-bpa-map-kind') === 'task'
      ) {
        return resolveTaskDefinitionFromNode(tasks, target, root);
      }

      return null;
    },
    [normalizedSvgContent, tasks],
  );

  const startMapPan = React.useCallback(
    (ev: React.PointerEvent) => {
      if (ev.button !== 0) {
        return;
      }

      ev.preventDefault();
      ev.currentTarget.setPointerCapture?.(ev.pointerId);
      const pointerTarget = ev.currentTarget;
      const pointerId = ev.pointerId;
      const pointerDownTarget = ev.target;
      suppressNextMapClickRef.current = false;
      cancelMapViewportAnimation();
      clearBpmnMapHoverState(inlineSvgRef.current);
      setIsPanning(true);

      const originalCursor = document.body.style.cursor;
      const originalUserSelect = document.body.style.userSelect;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      const startClientX = ev.clientX;
      const startClientY = ev.clientY;
      const startImageViewport = imageViewport;

      const handlePointerMove = (event: PointerEvent) => {
        const deltaX = event.clientX - startClientX;
        const deltaY = event.clientY - startClientY;

        if (Math.hypot(deltaX, deltaY) > 3) {
          suppressNextMapClickRef.current = true;
        }

        setImageViewport({
          ...startImageViewport,
          x: startImageViewport.x + deltaX,
          y: startImageViewport.y + deltaY,
        });
      };

      let handlePointerUp: () => void = () => undefined;
      let handlePointerCancel: () => void = () => undefined;

      const finishPointerInteraction = (selectTask: boolean) => {
        const wasClick = !suppressNextMapClickRef.current;

        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerCancel);
        document.body.style.cursor = originalCursor;
        document.body.style.userSelect = originalUserSelect;
        if (pointerTarget.hasPointerCapture?.(pointerId)) {
          pointerTarget.releasePointerCapture(pointerId);
        }
        setIsPanning(false);

        if (selectTask && wasClick) {
          const nextTask = resolveTaskFromMapEventTarget(pointerDownTarget);

          cancelPendingClickSelection();
          clickSelectionTimeoutRef.current = window.setTimeout(() => {
            setSelectedTaskId(taskPreviewEnabled ? nextTask?.id ?? null : null);
            clickSelectionTimeoutRef.current = null;
          }, CLICK_SELECTION_DELAY_MS);
          suppressNextMapClickRef.current = true;
        }
      };

      handlePointerUp = () => {
        finishPointerInteraction(true);
      };

      handlePointerCancel = () => {
        finishPointerInteraction(false);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp, { once: true });
      window.addEventListener('pointercancel', handlePointerCancel, {
        once: true,
      });
    },
    [
      cancelMapViewportAnimation,
      cancelPendingClickSelection,
      clearBpmnMapHoverState,
      imageViewport,
      resolveTaskFromMapEventTarget,
      taskPreviewEnabled,
    ],
  );

  const handleMapClick = React.useCallback(
    (ev: React.MouseEvent) => {
      if (ev.detail > 1) {
        cancelPendingClickSelection();
        suppressNextMapClickRef.current = false;
        return;
      }

      if (suppressNextMapClickRef.current || ev.detail !== 1) {
        suppressNextMapClickRef.current = false;
        return;
      }

      const nextTask = resolveTaskFromMapEventTarget(ev.target);

      cancelPendingClickSelection();
      clickSelectionTimeoutRef.current = window.setTimeout(() => {
        setSelectedTaskId(taskPreviewEnabled ? nextTask?.id ?? null : null);
        clickSelectionTimeoutRef.current = null;
      }, CLICK_SELECTION_DELAY_MS);
    },
    [cancelPendingClickSelection, resolveTaskFromMapEventTarget, taskPreviewEnabled],
  );

  const viewportClassName = `bpmGraphViewer__viewport ${
    isPanning ? 'bpmGraphViewer__viewport--panning' : ''
  } ${selectedTask ? 'bpmGraphViewer__viewport--hasTaskPreview' : ''}`.trim();

  return (
    <Box
      className={viewportClassName}
      onClick={handleMapClick}
      onPointerLeave={handleBpmnMapPointerLeave}
      onPointerMove={handleBpmnMapHover}
      onPointerDown={startMapPan}
      onDoubleClick={handleMapDoubleClick}
      onWheel={handleMapWheel}
    >
      <BPMGraphMapContent
        alt={alt}
        className={imageContentClassName}
        imageSrc={imageSrc}
        normalizedSvgContent={normalizedSvgContent}
        ref={inlineSvgRef}
        style={imageStyle}
      />
      <Box
        className="bpmGraphViewer__toolbar"
        onPointerDown={(ev) => ev.stopPropagation()}
      >
        <IconButton
          className="bpmGraphViewer__controlButton"
          iconSize="Sm"
          icon="Refresh"
          onClick={(ev) => {
            ev.stopPropagation();
            resetZoom();
          }}
          size="Lg"
          title={labels.resetZoom}
          variant="icon"
        />
      </Box>
      <BPMGraphSelectedTaskPreview
        attributes={attributes}
        entities={entities}
        formsByName={formsByName}
        renderTaskPreview={renderTaskPreview}
        selectedTask={selectedTask}
        taskPreviewEmpty={taskPreviewEmpty}
      />
      <Box className="bpmGraphViewer__legend" aria-hidden="true">
        <span className="bpmGraphViewer__legendItem bpmGraphViewer__legendItem--outgoing">
          <span className="bpmGraphViewer__legendSwatch" />
          <span>{labels.outgoingLegend}</span>
        </span>
        <span className="bpmGraphViewer__legendItem bpmGraphViewer__legendItem--incoming">
          <span className="bpmGraphViewer__legendSwatch" />
          <span>{labels.incomingLegend}</span>
        </span>
      </Box>
    </Box>
  );
};

export const BPMGraphViewer = React.memo(
  ({
    alt,
    attributes = [],
    className,
    entities = [],
    forms = [],
    fullscreen,
    fullscreenMode = 'portal',
    imageSrc,
    labels: userLabels,
    onFullscreenChange,
    renderTaskPreview,
    svgContent,
    taskEdgeHoverEnabled = true,
    taskPreviewEnabled = true,
    taskPreviewEmpty,
    tasks = [],
  }: TBPMGraphViewerProps) => {
    const [isMaximized, setIsMaximized] = React.useState(false);
    const effectiveIsMaximized =
      fullscreenMode === 'external' ? false : (fullscreen ?? isMaximized);
    const setFullscreen = React.useCallback(
      (next: boolean) => {
        onFullscreenChange?.(next);
        if (fullscreen === undefined && fullscreenMode !== 'external') {
          setIsMaximized(next);
        }
      },
      [fullscreen, fullscreenMode, onFullscreenChange],
    );
    const labels = React.useMemo(
      () => ({ ...DEFAULT_BPM_GRAPH_VIEWER_LABELS, ...userLabels }),
      [userLabels],
    );
    const effectiveSvgContent = React.useMemo(
      () => svgContent ?? svgContentFromImageSrc(imageSrc),
      [imageSrc, svgContent],
    );
    const effectiveImageSrc = React.useMemo(
      () =>
        imageSrc ??
        (effectiveSvgContent ? svgToDataUrl(effectiveSvgContent) : undefined),
      [effectiveSvgContent, imageSrc],
    );
    const normalizedSvgContent = React.useMemo(
      () =>
        effectiveSvgContent
          ? normalizeLegacyBpmnMapSvg(effectiveSvgContent)
          : undefined,
      [effectiveSvgContent],
    );

    React.useEffect(() => {
      if (!effectiveIsMaximized) {
        return undefined;
      }
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setFullscreen(false);
        }
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [effectiveIsMaximized, setFullscreen]);

    if (!effectiveImageSrc && !normalizedSvgContent) {
      return null;
    }

    const modal = effectiveIsMaximized ? (
      <Box
        className={`bpmGraphViewer__modal ${
          fullscreenMode === 'inline' ? 'bpmGraphViewer__modal--inline' : ''
        }`}
        role="dialog"
        aria-label={alt}
        onClick={() => setFullscreen(false)}
      >
        <IconButton
          className="bpmGraphViewer__modalClose bpmGraphViewer__controlButton"
          iconSize="Sm"
          icon="Close"
          onClick={() => setFullscreen(false)}
          size="Lg"
          title={labels.closeFullscreen}
          variant="icon"
        />
        <Box
          className="bpmGraphViewer__modalContent"
          onClick={(ev) => ev.stopPropagation()}
        >
          <BPMGraphImageViewport
            alt={alt}
            attributes={attributes}
            entities={entities}
            forms={forms}
            imageSrc={effectiveImageSrc}
            renderTaskPreview={renderTaskPreview}
            svgContent={svgContent}
            taskEdgeHoverEnabled={taskEdgeHoverEnabled}
            taskPreviewEnabled={taskPreviewEnabled}
            taskPreviewEmpty={taskPreviewEmpty}
            tasks={tasks}
          />
        </Box>
      </Box>
    ) : null;

    return (
      <BPMGraphViewerLabelsContext.Provider value={labels}>
        <Box
          className={`bpmGraphViewer ${className ?? ''}`.trim()}
          {...getVariant('layout.common.components.bpmGraphViewer')}
        >
          <Box className="bpmGraphViewer__thumbnailWrap">
            <button
              type="button"
              className="bpmGraphViewer__thumbnail"
              aria-label={`${alt}. ${labels.openFullscreen}`}
              title={labels.openFullscreen}
              onClick={() => setFullscreen(true)}
            >
              {normalizedSvgContent ? (
                <Box
                  as="span"
                  aria-hidden="true"
                  className="bpmGraphViewer__thumbnailSvg"
                  dangerouslySetInnerHTML={{ __html: normalizedSvgContent }}
                />
              ) : (
                <img alt="" src={effectiveImageSrc} />
              )}
            </button>
            <IconButton
              className="bpmGraphViewer__thumbnailAction bpmGraphViewer__controlButton"
              iconSize="Sm"
              icon="Maximize"
              onClick={() => setFullscreen(true)}
              size="Lg"
              title={labels.openFullscreen}
              variant="icon"
            />
          </Box>
          {modal && fullscreenMode === 'portal' && typeof document !== 'undefined'
            ? createPortal(
                <Box
                  className="bpmGraphViewer bpmGraphViewer--modalRoot"
                  {...getVariant('layout.common.components.bpmGraphViewer')}
                >
                  {modal}
                </Box>,
                document.body,
              )
            : modal}
        </Box>
      </BPMGraphViewerLabelsContext.Provider>
    );
  },
);

BPMGraphViewer.displayName = 'BPMGraphViewer';
